import React from "react";
import s from "../../style/home.module.less";
import { appUrl } from "../../lib/appBase";
import { TOOL_COUNT, TOOL_META, TOOL_FAMILIES } from "../../lib/toolMeta";
import { TOOL_PATHS } from "../../lib/slugs";
import { ToolTile } from "../common/ToolArt";

/* A curated preview of eight tools (two full rows of four) — the full set lives in the app,
   grouped by category, behind "See them all". Each card shows the tool's
   illustrated tile in its family's colour, as on /tools and in the app.

   Each tile carries two links: the tool name and "How it works" go to its
   /tools/<slug> landing page (app.<domain> is noindexed, so this is the
   home page's internal link to the tool's indexable content — that page
   in turn links deeper into /docs and out to the app). "Open tool" jumps
   straight into the app. `to` is a tool id (src/lib/slugs.js maps it to
   the URL); `slug` is the same id's keyword-rich slug, used for both
   /tools/ and /docs/. */
const FEATURED = [
  {
    key: "spl",
    name: "SPL Token Creator",
    to: "create-spl",
    slug: "create-solana-spl-token",
    purpose: "SPL token with metadata in one transaction.",
  },
  {
    key: "spl22",
    name: "Token-2022 Creator",
    to: "create-spl22",
    slug: "create-solana-token-2022",
    purpose: "Token-2022 with transfer fees and interest.",
  },
  {
    key: "multi",
    name: "Multisender",
    to: "multisender",
    slug: "solana-multisender",
    purpose: "Send to thousands of wallets from a CSV.",
  },
  {
    key: "manage",
    name: "Token Manager",
    to: "manage",
    slug: "solana-token-manager",
    purpose: "Mint, freeze, revoke, update metadata.",
  },
  {
    key: "snap",
    name: "Holder Snapshot",
    to: "snapshot",
    slug: "solana-token-holder-snapshot",
    purpose: "Export every holder as CSV.",
  },
  {
    key: "check",
    name: "Token Inspector",
    to: "token-inspector",
    slug: "solana-token-inspector",
    purpose: "Safety score, holders, LP lock and liquidity for any mint.",
  },
  {
    key: "drop",
    name: "Airdrop",
    to: "airdrop",
    slug: "solana-token-airdrop",
    purpose: "Send to every holder of a token, flat or pro-rata.",
  },
  {
    key: "rent",
    name: "Rent Reclaim",
    to: "rent-reclaim",
    slug: "reclaim-solana-rent",
    purpose: "Close empty accounts and get about 0.002 SOL back from each.",
  },
];

/* Every tool with a landing page, grouped the way the app groups them —
   the home page's one link to each /tools/<slug> page. Keyed off TOOL_META
   (dependency-free) rather than the app's catalog, so the home bundle
   doesn't pull in the tool icons. Each family is a card saying what it's
   for; the cards sit in three hand-balanced columns (by tool count) so
   none is left with a hole under it, and collapse to one on phones. */
const FAMILY_BLURB = {
  create: "Make a new token",
  launchpads: "Launch on a bonding curve",
  market: "Pools, positions and market-making bots",
  distribute: "Send tokens to many wallets",
  manage: "Control a token you hold",
  locks: "Lock and vest tokens",
  analyze: "Research a token or wallet",
  utilities: "Keys, signatures and test SOL",
};
// One short line per tool, shorter than TOOL_META's description so the
// directory reads as a list.
const TAGLINE = {
  "create-spl": "Standard token with name, logo and supply",
  "create-spl22": "Transfer tax, interest, on-mint metadata",
  launchpad: "Escrowed presale or fair launch",
  launch: "Create on pump.fun with your first buy",
  "launch-bonk": "letsbonk.fun or Raydium LaunchLab",
  "launch-meteora": "Meteora curve plus your first buy",
  stonkfun: "Pair your coin with a tokenized stock",
  "creator-fees": "Claim pump.fun, LaunchLab, Meteora fees",
  "add-liquidity": "Deposit on Raydium, Meteora, Orca, PumpSwap",
  "remove-liquidity": "Every LP position in your wallet, withdrawn",
  "grid-bot": "Buys the dips, sells one line higher",
  "liquidity-bot": "A DLMM position that re-centres itself",
  multisender: "Thousands of wallets, one signature",
  airdrop: "Every holder of a token, flat or pro-rata",
  "wallet-aggregator": "Pull tokens from many wallets into one",
  manage: "Mint, revoke, transfer, edit metadata",
  burn: "Destroy tokens from your wallet",
  "rent-reclaim": "Close empty accounts, about 0.002 SOL each",
  "wallet-cleaner": "Reclaim rent across many wallets",
  "token-locker": "Time-lock or vest any token",
  "token-lock-verifier": "Every lock on a mint, and when it opens",
  "lp-locker": "Lock LP tokens until a public date",
  "lp-lock-verifier": "Is the LP locked, how much, until when",
  snapshot: "Every holder as CSV, pools marked",
  "my-tokens": "Every token your wallet created",
  "token-inspector": "A 0–100 rug check for any mint",
  "smart-money": "Do the top holders trade well elsewhere?",
  "stonkfun-rewards": "What your StonkFun coins have paid",
  "tx-inspector": "Any transaction, step by step in words",
  account: "What any address is and holds",
  status: "Live TPS, priority fee and epoch",
  sign: "Prove you own a wallet",
  vanity: "An address with your text in it",
  faucet: "Free test SOL for devnet",
};
// Not on mainnet yet (the app's catalog tags them the same way).
const DEVNET_ONLY = new Set(["launchpad", "token-locker", "token-lock-verifier", "lp-locker", "lp-lock-verifier"]);
const COLUMNS = [
  ["create", "distribute", "manage"],
  ["launchpads", "market", "locks"],
  ["analyze", "utilities"],
];
const family = (key) => ({
  key,
  label: TOOL_FAMILIES[key].label,
  color: TOOL_FAMILIES[key].color,
  tools: Object.entries(TOOL_META)
    .filter(([id, m]) => id !== "app" && m.family === key && TOOL_PATHS[id])
    .map(([id, m]) => ({ id, title: m.title, slug: TOOL_PATHS[id] })),
});
const DIRECTORY = COLUMNS.map((keys) => keys.map(family).filter((g) => g.tools.length > 0));

export const ToolExplorer = () => (
  <section id="tools" className={s.section}>
    <div className={s.catToolsHead}>
      <div>
        <span className="ds-eyebrow">The toolkit</span>
        <h2 className={s.sectionTitle}>
          {TOOL_COUNT} tools.
          <br />
          One wallet connection.
        </h2>
      </div>
      <a href="/tools" className="ds-btn ds-btn--secondary">
        See them all
      </a>
    </div>

    <div className={s.catGrid} style={{ marginTop: 28 }}>
      {FEATURED.map((t) => (
        <article key={t.key} className={s.tool}>
          <ToolTile id={t.to} color={TOOL_FAMILIES[TOOL_META[t.to].family].color} />
          <h3 className={s.toolName}>
            <a href={`/tools/${t.slug}`} className={s.toolNameLink}>
              {t.name}
            </a>
          </h3>
          <p className={s.toolText}>{t.purpose}</p>
          <div className={s.toolLinks}>
            <a href={appUrl(t.to)} rel="nofollow" className={s.toolCta}>
              Open tool <span aria-hidden="true">→</span>
            </a>
            <a href={`/tools/${t.slug}`} className={s.toolDocsLink}>
              How it works
            </a>
          </div>
        </article>
      ))}
    </div>

    <nav className={s.dir} aria-labelledby="all-tools-h">
      <div className={s.dirHead}>
        <h3 className={s.dirTitle} id="all-tools-h">
          All {TOOL_COUNT} Solana token tools
        </h3>
        <p className={s.dirSub}>Grouped by what you're trying to do. Each one has its own page with a guide and FAQ.</p>
      </div>
      <div className={s.dirCols}>
        {DIRECTORY.map((col, i) => (
          <div key={i} className={s.dirCol}>
            {col.map((g) => (
              <section key={g.key} className={s.dirFam} style={{ "--fam": g.color }}>
                <header className={s.dirFamHead}>
                  <span className={s.dirFamLabel}>{g.label}</span>
                  <span className={s.dirFamBlurb}>{FAMILY_BLURB[g.key]}</span>
                  <span className={s.dirFamCount}>{g.tools.length}</span>
                </header>
                <ul className={s.dirList}>
                  {g.tools.map((t) => (
                    <li key={t.id}>
                      <a href={`/tools/${t.slug}`} className={s.dirLink}>
                        <span className={s.dirName}>
                          {t.title}
                          {DEVNET_ONLY.has(t.id) && <span className={s.dirTag}>Devnet</span>}
                        </span>
                        <span className={s.dirLine}>{TAGLINE[t.id]}</span>
                        <span className={s.dirArrow} aria-hidden="true">
                          →
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ))}
      </div>
    </nav>
  </section>
);
